import React from 'react';
import { Chip, alpha } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import InventoryIcon from '@mui/icons-material/Inventory';
import { OrderFulfillmentStatus } from '../../types';
import { palette, typography, theme as themeConfig } from '../../theme/colors';

interface FulfillmentStatusChipProps {
  status: OrderFulfillmentStatus;
  size?: 'small' | 'medium';
}

interface StatusConfig {
  color: string;
  label: string;
  icon: React.ReactElement;
}

const getFulfillmentStatusConfig = (status: OrderFulfillmentStatus): StatusConfig => {
  switch (status) {
    case OrderFulfillmentStatus.PENDING:
      return {
        color: palette.warning,
        label: 'EN ATTENTE',
        icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.WAITING_TRANSFER:
      return {
        color: palette.info,
        label: 'ATTENTE TRANSFERT',
        icon: <SyncIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.TRANSFER_ISSUE:
      return {
        color: palette.error,
        label: 'PROBLÈME TRANSFERT',
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.WAITING_ADMIN_ACTION:
      return {
        color: palette.warning,
        label: 'ATTENTE ADMIN',
        icon: <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.CONFIRMED:
      return {
        color: palette.success,
        label: 'CONFIRMÉ',
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.READY_FOR_PICKUP:
      return {
        color: palette.accent,
        label: 'PRÊT À RETIRER',
        icon: <StorefrontIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.READY_FOR_DELIVERY:
      return {
        color: palette.accent,
        label: 'PRÊT À LIVRER',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.OUT_FOR_DELIVERY:
      return {
        color: palette.info,
        label: 'EN LIVRAISON',
        icon: <DirectionsCarIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.DELIVERED:
      return {
        color: palette.success,
        label: 'LIVRÉ',
        icon: <HomeIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.PICKED_UP:
      return {
        color: palette.success,
        label: 'RETIRÉ',
        icon: <ShoppingBagIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.COMPLETED:
      return {
        color: palette.success,
        label: 'TERMINÉ',
        icon: <DoneAllIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.CANCELLED:
      return {
        color: palette.error,
        label: 'ANNULÉ',
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
      };
    case OrderFulfillmentStatus.EXPIRED:
      return {
        color: palette.textMuted,
        label: 'EXPIRÉ',
        icon: <TimerOffIcon sx={{ fontSize: 16 }} />,
      };
    default:
      return {
        color: palette.textMuted,
        label: String(status).toUpperCase(),
        icon: <InventoryIcon sx={{ fontSize: 16 }} />,
      };
  }
};

export const FulfillmentStatusChip: React.FC<FulfillmentStatusChipProps> = ({
  status,
  size = 'small'
}) => {
  const config = getFulfillmentStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size={size}
      sx={{
        backgroundColor: alpha(config.color, 0.1),
        color: config.color,
        fontFamily: typography.fontFamily.mono,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.bold,
        border: `1px solid ${config.color}`,
        textTransform: 'uppercase',
        borderRadius: themeConfig.radius.sm,
        '& .MuiChip-icon': {
          color: config.color,
        },
      }}
    />
  );
};

export default FulfillmentStatusChip;
